import {Card, DataStatus, Separator, Tooltip} from 'platform/components';
import {Grid, Heading, HStack, Text, VStack, Icon, Show} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNil} from 'ramda';

import {useGetHistoryOrderProfitabilityQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {numberToTimeString} from '../../utils/numberToTimeString';

interface ProfitabilityHistoryProps extends RequiredTestIdProps {
  vehicleId: string | Nullish;
}

export function ProfitabilityHistory(props: ProfitabilityHistoryProps) {
  const formatPrice = useFormatCurrency();

  const {data, isLoading, isError} = useGetHistoryOrderProfitabilityQuery(
    {
      vehicleId: props.vehicleId ?? '',
    },
    {skip: isNil(props.vehicleId)}
  );

  return (
    <DataStatus isLoading={isLoading} isError={isError} isEmpty={isNil(data)}>
      <VStack spacing={4}>
        <Card title={i18n.t('general.labels.summary')}>
          <VStack spacing={4}>
            <Grid columns={4}>
              <VStack align="flex-start" spacing={1}>
                <HStack align="center" spacing={1}>
                  <Text size="small" color="secondary">
                    {i18n.t('entity.profitability.labels.totalServiceCases')}
                  </Text>
                  <Tooltip
                    description={i18n.t('entity.profitability.labels.totalServiceCasesTooltip')}
                  >
                    <Icon size={4} value="action/help" />
                  </Tooltip>
                </HStack>
                <Heading size={3}>
                  {data?.summary?.serviceCaseCount
                    ? String(data.summary.serviceCaseCount)
                    : EMPTY_PLACEHOLDER}
                </Heading>
              </VStack>

              <VStack align="flex-start" spacing={1}>
                <HStack align="center" spacing={1}>
                  <Text size="small" color="secondary">
                    {i18n.t('entity.profitability.labels.totalServiceOrders')}
                  </Text>
                  <Tooltip
                    description={i18n.t('entity.profitability.labels.totalServiceOrdersTooltip')}
                  >
                    <Icon size={4} value="action/help" />
                  </Tooltip>
                </HStack>
                <Heading size={3}>
                  {data?.summary?.serviceOrderCount
                    ? String(data.summary.serviceOrderCount)
                    : EMPTY_PLACEHOLDER}
                </Heading>
              </VStack>

              <VStack align="flex-start" spacing={1}>
                <Text size="small" color="secondary">
                  {i18n.t('entity.profitability.labels.totalSoldHours')}
                </Text>
                <Heading size={3}>{numberToTimeString(data?.summary?.soldHours || 0)}</Heading>
              </VStack>
            </Grid>

            <Separator />

            <Grid columns={4} spacing={4}>
              <VStack align="flex-start" spacing={1}>
                <Text size="small" color="secondary">
                  {i18n.t('entity.profitability.labels.totalRevenue')}
                </Text>
                <Heading size={3}>
                  {data?.summary?.totalRevenue?.withoutVat
                    ? formatPrice(
                        data?.summary?.totalRevenue?.withoutVat,
                        data?.summary?.currency,
                        2
                      )
                    : EMPTY_PLACEHOLDER}
                </Heading>
                <Text size="small" color="secondary">
                  {data?.summary?.totalRevenue?.withVat
                    ? formatPrice(data?.summary?.totalRevenue?.withVat, data?.summary?.currency, 2)
                    : EMPTY_PLACEHOLDER}{' '}
                  {i18n.t('general.labels.withVat')}
                </Text>
              </VStack>

              <VStack align="flex-start" spacing={1}>
                <Text size="small" color="secondary">
                  {i18n.t('entity.profitability.labels.labourRevenue')}
                </Text>
                <Heading size={3}>
                  {data?.summary?.labourRevenue?.withoutVat
                    ? formatPrice(
                        data?.summary?.labourRevenue?.withoutVat,
                        data?.summary?.currency,
                        2
                      )
                    : EMPTY_PLACEHOLDER}
                </Heading>
                <Text size="small" color="secondary">
                  {data?.summary?.labourRevenue?.withVat
                    ? formatPrice(data?.summary?.labourRevenue?.withVat, data?.summary?.currency, 2)
                    : EMPTY_PLACEHOLDER}{' '}
                  {i18n.t('general.labels.withVat')}
                </Text>
              </VStack>

              <VStack align="flex-start" spacing={1}>
                <Text size="small" color="secondary">
                  {i18n.t('entity.profitability.labels.materialRevenue')}
                </Text>
                <Heading size={3}>
                  {data?.summary?.materialRevenue?.withoutVat
                    ? formatPrice(
                        data?.summary?.materialRevenue?.withoutVat,
                        data?.summary?.currency,
                        2
                      )
                    : EMPTY_PLACEHOLDER}
                </Heading>
                <Text size="small" color="secondary">
                  {data?.summary?.materialRevenue?.withVat
                    ? formatPrice(
                        data?.summary?.materialRevenue?.withVat,
                        data?.summary?.currency,
                        2
                      )
                    : EMPTY_PLACEHOLDER}{' '}
                  {i18n.t('general.labels.withVat')}
                </Text>
              </VStack>

              <VStack align="flex-start" spacing={1}>
                <Text size="small" color="secondary">
                  {i18n.t('entity.profitability.labels.smallUnitMaterialRevenue')}
                </Text>
                <Heading size={3}>
                  {data?.summary?.smallUnitMaterialRevenue?.withoutVat
                    ? formatPrice(
                        data?.summary?.smallUnitMaterialRevenue?.withoutVat,
                        data?.summary?.currency,
                        2
                      )
                    : EMPTY_PLACEHOLDER}
                </Heading>
                <Text size="small" color="secondary">
                  {data?.summary?.smallUnitMaterialRevenue?.withVat
                    ? formatPrice(
                        data?.summary?.smallUnitMaterialRevenue?.withVat,
                        data?.summary?.currency,
                        2
                      )
                    : EMPTY_PLACEHOLDER}{' '}
                  {i18n.t('general.labels.withVat')}
                </Text>
              </VStack>
            </Grid>
          </VStack>
        </Card>

        <Show when={data?.history?.length}>
          <Card
            title={i18n.t('general.labels.history')}
            isExpandable
            isClosedByDefault
            flags={
              data?.history?.length
                ? [{label: String(data.history.length), colorScheme: 'blue', isSubtle: true}]
                : []
            }
            data-testid={suffixTestId('history', props)}
          >
            <VStack spacing={4}>
              {data?.history?.map((yearData) => (
                <Card variant="inlineGrey" key={yearData.year}>
                  <VStack spacing={4}>
                    <HStack
                      justify="space-between"
                      data-testid={suffixTestId(`history-${yearData.year}-header`, props)}
                    >
                      <Heading size={4}>{String(yearData.year)}</Heading>
                    </HStack>
                    <VStack spacing={4}>
                      <Grid columns={4} spacing={4}>
                        <VStack align="flex-start" spacing={1}>
                          <Text size="small" color="secondary">
                            {i18n.t('entity.profitability.labels.totalServiceCases')}
                          </Text>
                          <Heading size={3}>{String(yearData.serviceCaseCount)}</Heading>
                        </VStack>

                        <VStack align="flex-start" spacing={1}>
                          <Text size="small" color="secondary">
                            {i18n.t('entity.profitability.labels.totalServiceOrders')}
                          </Text>
                          <Heading size={3}>{String(yearData.serviceOrderCount)}</Heading>
                        </VStack>

                        <VStack align="flex-start" spacing={1}>
                          <Text size="small" color="secondary">
                            {i18n.t('entity.profitability.labels.totalSoldHours')}
                          </Text>
                          <Heading size={3}>{numberToTimeString(yearData.soldHours || 0)}</Heading>
                        </VStack>
                      </Grid>

                      <Separator />

                      <Grid columns={4} spacing={4}>
                        <VStack align="flex-start" spacing={1}>
                          <Text size="small" color="secondary">
                            {i18n.t('entity.profitability.labels.totalRevenue')}
                          </Text>
                          <Heading size={3}>
                            {formatPrice(yearData.totalRevenue.withoutVat, yearData.currency, 2)}
                          </Heading>
                          <Text size="small" color="secondary">
                            {`${formatPrice(yearData.totalRevenue.withVat, yearData.currency, 2)} ${i18n.t('general.labels.withVat')}`}
                          </Text>
                        </VStack>

                        <VStack align="flex-start" spacing={1}>
                          <Text size="small" color="secondary">
                            {i18n.t('entity.profitability.labels.labourRevenue')}
                          </Text>
                          <Heading size={3}>
                            {formatPrice(yearData.labourRevenue.withoutVat, yearData.currency, 2)}
                          </Heading>
                          <Text size="small" color="secondary">
                            {`${formatPrice(yearData.labourRevenue.withVat, yearData.currency, 2)} ${i18n.t('general.labels.withVat')}`}
                          </Text>
                        </VStack>

                        <VStack align="flex-start" spacing={1}>
                          <Text size="small" color="secondary">
                            {i18n.t('entity.profitability.labels.materialRevenue')}
                          </Text>
                          <Heading size={3}>
                            {formatPrice(yearData.materialRevenue.withoutVat, yearData.currency, 2)}
                          </Heading>
                          <Text size="small" color="secondary">
                            {`${formatPrice(yearData.materialRevenue.withVat, yearData.currency, 2)} ${i18n.t('general.labels.withVat')}`}
                          </Text>
                        </VStack>

                        <VStack align="flex-start" spacing={1}>
                          <Text size="small" color="secondary">
                            {i18n.t('entity.profitability.labels.smallUnitMaterialRevenue')}
                          </Text>
                          <Heading size={3}>
                            {formatPrice(
                              yearData.smallUnitMaterialRevenue.withoutVat,
                              yearData.currency,
                              2
                            )}
                          </Heading>
                          <Text size="small" color="secondary">
                            {`${formatPrice(
                              yearData.smallUnitMaterialRevenue.withVat,
                              yearData.currency,
                              2
                            )} ${i18n.t('general.labels.withVat')}`}
                          </Text>
                        </VStack>
                      </Grid>
                    </VStack>
                  </VStack>
                </Card>
              ))}
            </VStack>
          </Card>
        </Show>
      </VStack>
    </DataStatus>
  );
}
